import { parsePriceToKobo, koboToInputValue } from '../../shared/utils/priceUtils';
import { RateType, ZoneType, type CreateShippingZonePayload, type ShippingZone, type WeightTier } from './api/shippingZonesApi';

/**
 * Pure form ⇄ DTO mapping + validation for the shipping-zone form (PE-2172/PE-2173).
 * Kept free of React/PriceInput so the rules — kobo conversion, LOCAL_PICKUP forcing a
 * free rate, ascending weight tiers, ETA ordering — are unit-testable without mounting
 * a component. See payload.test.ts.
 */

export interface WeightTierFormValues {
  /** Grams, as typed. Blank on the last row means "and above". */
  maxWeightGrams: string;
  /** Naira, as typed/formatted (e.g. "2,500.00") — converted via parsePriceToKobo. */
  amountNaira: string;
}

export interface ZoneFormValues {
  name: string;
  type: ZoneType;
  /** Ignored entirely for LOCAL_PICKUP. */
  countryCode: string;
  /** true ⇒ country-wide catch-all (stateOrProvinceCodes: []); only meaningful for REGION. */
  allStates: boolean;
  stateCodes: string[];
  isActive: boolean;
  /** Numeric string; blank ⇒ 0. */
  priority: string;
  /** Numeric strings; blank ⇒ omitted. */
  etaMinDays: string;
  etaMaxDays: string;
  /** Ignored entirely for LOCAL_PICKUP (always forced to FREE). */
  rateType: RateType;
  /** Naira, as typed — only used when rateType is FLAT. */
  flatAmountNaira: string;
  /** Only used when rateType is WEIGHT_BASED. */
  weightTiers: WeightTierFormValues[];
}

export const emptyWeightTier = (): WeightTierFormValues => ({ maxWeightGrams: '', amountNaira: '' });

export const DEFAULT_ZONE_FORM_VALUES: ZoneFormValues = {
  name: '',
  type: ZoneType.REGION,
  countryCode: 'NG',
  allStates: true,
  stateCodes: [],
  isActive: true,
  priority: '0',
  etaMinDays: '',
  etaMaxDays: '',
  rateType: RateType.FLAT,
  flatAmountNaira: '',
  weightTiers: [emptyWeightTier()],
};

const toIntOrUndefined = (raw: string): number | undefined => {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const n = parseInt(trimmed, 10);
  return Number.isNaN(n) ? undefined : n;
};

/**
 * Validates the form well enough to avoid a 400 round trip. Returns an empty array
 * when the form is submittable.
 */
export function validateZoneForm(values: ZoneFormValues): string[] {
  const errors: string[] = [];

  if (!values.name.trim()) {
    errors.push('Zone name is required.');
  }

  if (values.type === ZoneType.REGION) {
    if (!values.countryCode.trim()) {
      errors.push('Country is required for a region zone.');
    }
    if (!values.allStates && values.stateCodes.length === 0) {
      errors.push('Choose at least one state, or turn on "All states in country".');
    }
  }

  const min = toIntOrUndefined(values.etaMinDays);
  const max = toIntOrUndefined(values.etaMaxDays);
  if (min !== undefined && max !== undefined && min > max) {
    errors.push('Minimum delivery estimate cannot be greater than the maximum.');
  }

  // LOCAL_PICKUP is always free — rate validation only applies to REGION.
  if (values.type === ZoneType.REGION) {
    if (values.rateType === RateType.FLAT) {
      if (parsePriceToKobo(values.flatAmountNaira) <= 0) {
        errors.push('Enter a flat rate greater than ₦0.');
      }
    } else if (values.rateType === RateType.WEIGHT_BASED) {
      errors.push(...validateWeightTiers(values.weightTiers));
    }
  }

  return errors;
}

/** Isolated so ShippingZoneForm can surface tier-specific errors inline too. */
export function validateWeightTiers(tiers: WeightTierFormValues[]): string[] {
  const errors: string[] = [];

  if (tiers.length === 0) {
    errors.push('Add at least one weight tier.');
    return errors;
  }

  let previousMax = -Infinity;
  tiers.forEach((tier, i) => {
    const isLast = i === tiers.length - 1;
    const max = toIntOrUndefined(tier.maxWeightGrams);

    if (max === undefined && !isLast) {
      errors.push(`Tier ${i + 1} must have a maximum weight — only the last tier may say "and above".`);
    }
    if (max !== undefined && max <= previousMax) {
      errors.push(`Tier ${i + 1}'s maximum weight must be greater than the previous tier's.`);
    }
    if (max !== undefined) previousMax = max;

    if (parsePriceToKobo(tier.amountNaira) < 0) {
      errors.push(`Tier ${i + 1} needs a valid price.`);
    }
  });

  return errors;
}

const buildWeightTiers = (tiers: WeightTierFormValues[]): WeightTier[] =>
  tiers.map((t, i) => {
    const isLast = i === tiers.length - 1;
    const max = toIntOrUndefined(t.maxWeightGrams);
    const out: WeightTier = { amountMinor: parsePriceToKobo(t.amountNaira) };
    // Only the last tier may omit maxWeightGrams ("and above") — earlier blanks would
    // have failed validateWeightTiers, but stay defensive rather than emit `undefined`.
    if (max !== undefined || !isLast) {
      out.maxWeightGrams = max;
    }
    return out;
  });

/**
 * Maps validated form state to the create/update DTO. Call after validateZoneForm
 * returns no errors — this does not re-validate.
 */
export function buildZonePayload(values: ZoneFormValues): CreateShippingZonePayload {
  const priority = toIntOrUndefined(values.priority) ?? 0;
  const deliveryEstimateMinDays = toIntOrUndefined(values.etaMinDays);
  const deliveryEstimateMaxDays = toIntOrUndefined(values.etaMaxDays);

  const base = {
    name: values.name.trim(),
    type: values.type,
    isActive: values.isActive,
    priority,
    ...(deliveryEstimateMinDays !== undefined && { deliveryEstimateMinDays }),
    ...(deliveryEstimateMaxDays !== undefined && { deliveryEstimateMaxDays }),
  };

  if (values.type === ZoneType.LOCAL_PICKUP) {
    // LOCAL_PICKUP zones drop country/state entirely and are always free — the backend
    // still requires a `rate` object on create even though it's ignored.
    return { ...base, rate: { type: RateType.FREE, currency: 'NGN' } };
  }

  const rate: CreateShippingZonePayload['rate'] =
    values.rateType === RateType.FLAT
      ? { type: RateType.FLAT, flatAmountMinor: parsePriceToKobo(values.flatAmountNaira), currency: 'NGN' }
      : values.rateType === RateType.WEIGHT_BASED
        ? { type: RateType.WEIGHT_BASED, weightTiers: buildWeightTiers(values.weightTiers), currency: 'NGN' }
        : { type: RateType.FREE, currency: 'NGN' };

  return {
    ...base,
    countryCode: values.countryCode,
    // "All states" ⇒ empty array = country-wide catch-all, never an explicit list.
    stateOrProvinceCodes: values.allStates ? [] : values.stateCodes,
    rate,
  };
}

/** Inverse of buildZonePayload, for populating the form when editing an existing zone. */
export function toFormValues(zone: ShippingZone): ZoneFormValues {
  const rate = zone.rate;
  return {
    name: zone.name,
    type: zone.type,
    countryCode: zone.countryCode ?? 'NG',
    allStates: zone.type === ZoneType.LOCAL_PICKUP || zone.stateOrProvinceCodes.length === 0,
    stateCodes: zone.stateOrProvinceCodes,
    isActive: zone.isActive,
    priority: String(zone.priority),
    etaMinDays: zone.deliveryEstimateMinDays != null ? String(zone.deliveryEstimateMinDays) : '',
    etaMaxDays: zone.deliveryEstimateMaxDays != null ? String(zone.deliveryEstimateMaxDays) : '',
    rateType: rate.type,
    flatAmountNaira: rate.type === RateType.FLAT && rate.flatAmountMinor != null ? koboToInputValue(rate.flatAmountMinor) : '',
    weightTiers:
      rate.type === RateType.WEIGHT_BASED && rate.weightTiers?.length
        ? rate.weightTiers.map((t) => ({
            maxWeightGrams: t.maxWeightGrams != null ? String(t.maxWeightGrams) : '',
            amountNaira: koboToInputValue(t.amountMinor),
          }))
        : [emptyWeightTier()],
  };
}
